/**
 * @fileoverview TradingView adapter exposing CAP-native interface.
 * Consumers use this module, never TradingView directly.
 * @module external/tradingview/adapter
 */

import { loadTradingView, isTradingViewLoaded } from './loader.js';
import { TV_THEME, SIGNAL_TO_SYMBOL } from './config.js';

/**
 * Creates a market chart instance.
 * Lazily loads TradingView if not already loaded.
 * 
 * @param {HTMLElement} container - DOM element to render into (must have an id)
 * @param {import('./types.js').MarketChartConfig} config - Chart configuration
 * @returns {Promise<import('./types.js').MarketChartInstance>}
 * @throws {Error} If container has no id or TradingView fails to load
 * 
 * @example
 * const chart = await createMarketChart(document.getElementById('chart'), {
 *   symbol: 'CBOE:VIX',
 *   interval: 'D'
 * });
 * 
 * // Later
 * chart.setSymbol('TVC:DXY');
 * chart.destroy();
 */
export async function createMarketChart(container, config = {}) {
  // Validate container
  if (!container) {
    throw new Error('[TradingView] Container element is required');
  }
  
  if (!container.id) {
    // Generate id if not present
    container.id = `tv-chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  
  // Load TradingView script
  await loadTradingView();
  
  if (!isTradingViewLoaded()) {
    throw new Error('[TradingView] Library failed to initialize');
  }
  
  // Resolve symbol - accept CAP signal ID or direct TV symbol
  const resolvedSymbol = resolveSymbol(config.symbol || 'CBOE:VIX');
  
  console.log(`[TradingView] Creating chart: ${resolvedSymbol} in #${container.id}`);
  
  // Create widget with merged config
  const widgetConfig = {
    ...TV_THEME,
    container_id: container.id,
    symbol: resolvedSymbol,
    interval: config.interval || 'D',
    allow_symbol_change: config.allowSymbolChange !== false,
    studies: config.studies || [],
    width: '100%',
    height: '100%',
  };
  
  // @ts-ignore - TradingView is loaded dynamically
  const widget = new window.TradingView.widget(widgetConfig);
  
  // Track current symbol for getter
  let currentSymbol = resolvedSymbol;
  
  // Return CAP-native interface
  return {
    /**
     * Destroys the chart and cleans up.
     */
    destroy() {
      console.log(`[TradingView] Destroying chart in #${container.id}`);
      try {
        widget.remove();
      } catch (e) {
        // Widget may already be removed
        console.warn('[TradingView] Widget already removed or failed to remove');
      }
      container.innerHTML = '';
    },
    
    /**
     * Changes the displayed symbol.
     * @param {string} symbol - CAP signal ID or TradingView symbol
     */
    setSymbol(symbol) {
      const resolved = resolveSymbol(symbol);
      currentSymbol = resolved;
      console.log(`[TradingView] Changing symbol to: ${resolved}`);
      // Note: TradingView widget requires iframe postMessage for symbol change
      // For simplicity, we recreate the widget
      container.innerHTML = '';
      // @ts-ignore
      new window.TradingView.widget({
        ...widgetConfig,
        symbol: resolved
      });
    },
    
    /**
     * Gets the current symbol.
     * @returns {string}
     */
    getSymbol() {
      return currentSymbol;
    }
  };
}

/**
 * Resolves a CAP signal ID to TradingView symbol.
 * If already a TV symbol (contains ':'), returns as-is.
 * 
 * @param {string} symbolOrSignal - CAP signal ID or TV symbol
 * @returns {string} TradingView symbol
 */
export function resolveSymbol(symbolOrSignal) {
  // If it's already a TradingView symbol (has exchange prefix)
  if (symbolOrSignal.includes(':')) {
    return symbolOrSignal;
  }
  
  // Look up in signal mapping
  const mapping = SIGNAL_TO_SYMBOL[symbolOrSignal];
  if (mapping) {
    return mapping.tvSymbol;
  }
  
  // Fallback: assume it's a direct symbol
  console.warn(`[TradingView] Unknown signal "${symbolOrSignal}", using as direct symbol`);
  return symbolOrSignal;
}

/**
 * Gets symbol info for a CAP signal.
 * @param {string} signalId - CAP signal ID
 * @returns {import('./types.js').SymbolMapping|null}
 */
export function getSymbolInfo(signalId) {
  return SIGNAL_TO_SYMBOL[signalId] || null;
}

/**
 * Creates a fallback element when TradingView fails to load.
 * @param {HTMLElement} container
 * @param {string} message
 * @returns {HTMLElement}
 */
export function renderFallback(container, message = 'Market chart unavailable') {
  container.innerHTML = `
    <div class="tv-fallback" style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-height: 300px;
      background: rgba(20, 24, 28, 0.8);
      border: 1px solid rgba(151, 184, 160, 0.15);
      border-radius: 8px;
      color: rgba(151, 184, 160, 0.7);
      font-family: 'JetBrains Mono', monospace;
    ">
      <div style="font-size: 2rem; margin-bottom: 1rem;">📊</div>
      <div>${message}</div>
      <a href="https://www.tradingview.com/" 
         target="_blank" 
         rel="noopener noreferrer"
         style="margin-top: 1rem; color: #D4A85F;">
        Open TradingView →
      </a>
    </div>
  `;
  return container;
}
